import { css, styled } from 'styled-components';

import { VersionedRouterLink } from '#/components/VersionedLink/VersionedRouterLink';
import { scrolledDownHiddenVar, scrolledDownZeroVar } from '#/theme/scrolled';
import { colors, cssBreakpoints, gutterWidth, transition } from '#/theme/theme';

const pageHeaderTopSectionHeightVar = '--page-header-top-section-height';
const pageHeaderSearchPageBaseHeight = 130;
const pageHeaderMobileLinksHeight = 64;

export const PageHeaderHolder = styled.div<{ $isInSearchPage: boolean }>`
  position: sticky;
  top: 0;
  left: 0;
  z-index: 101;

  @media print {
    display: none;
  }

  ${(props) =>
    props.$isInSearchPage &&
    css`
      @media all and (max-width: ${cssBreakpoints.mdDown}) {
        margin-bottom: calc(
          ${pageHeaderMobileLinksHeight}px *
            (var(${scrolledDownZeroVar}, -1) + 1)
        );
        height: calc(
          ${pageHeaderSearchPageBaseHeight}px +
            ${pageHeaderMobileLinksHeight}px * var(${scrolledDownZeroVar}, 1)
        );
      }
    `};
`;

export const PageHeaderInnerHolder = styled.div`
  display: flex;
  flex-direction: column;
  background: ${colors.white};
  box-shadow: 0 2px 4px rgb(0 0 0 / 7.5%);
  font-size: 15px;
  line-height: normal;

  /* Hide box-shadow when search filters are present */
  &:has(+ div > form[role='search']) {
    box-shadow: none;
  }
`;

export const PageHeaderTopSectionHolder = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${gutterWidth / 2}px;
  height: var(${pageHeaderTopSectionHeightVar});
  position: relative;

  @media screen and (max-width: ${cssBreakpoints.mdDown}) {
    ${pageHeaderTopSectionHeightVar}: 60px;
  }

  @media screen and (min-width: ${cssBreakpoints.mdUp}) {
    ${pageHeaderTopSectionHeightVar}: 58px;
  }
`;

export const PageHeaderBottomSectionHolder = styled.div<{
  $isInSearchPage: boolean;
  $withScrolledTransition: boolean;
}>`
  display: flex;
  flex-direction: column;

  @media all and (max-width: ${cssBreakpoints.mdDown}) {
    ${(props) =>
      props.$isInSearchPage
        ? css`
            transition: ${props.$withScrolledTransition &&
            `transform ${transition}`};
            transform: translateY(
              calc(
                ${pageHeaderMobileLinksHeight}px *
                  var(${scrolledDownZeroVar}, 1)
              )
            );
          `
        : css`
            padding-top: ${pageHeaderMobileLinksHeight}px;
          `};
  }
`;

export const PageHeaderLeft = styled.div`
  display: flex;
  align-items: center;
`;

export const PageHeaderLinksHolder = styled.div<{ $isInSearchPage: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: ${cssBreakpoints.mdDown}) {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    height: ${pageHeaderMobileLinksHeight}px;
    border-top: 1px solid ${colors.selectionGrey};
    justify-content: space-evenly;
    gap: 0;
  }

  ${(props) =>
    props.$isInSearchPage &&
    css`
      @media all and (max-width: ${cssBreakpoints.mdDown}) {
        transition: height ${transition};
        height: calc(
          ${pageHeaderMobileLinksHeight}px * var(${scrolledDownZeroVar}, 1)
        );
        overflow: var(${scrolledDownHiddenVar});
      }
    `};
`;

// Only display the API & Integrations button on desktop up to a certain point
// This is a magical number - it was just a rough estimate of what fits in the current UI
const apiIntegrationsDropdownCutoffPoint = '1140px';

export const PageHeaderApiAndIntegrationsDropdownHolder = styled.div`
  @media (max-width: ${apiIntegrationsDropdownCutoffPoint}) {
    display: none;
  }
`;

export const PageHeaderDesktopDonateLinkHolder = styled.div`
  @media (max-width: ${cssBreakpoints.mdDown}) {
    display: none;
  }
`;

export const PageHeaderRight = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

export const PageHeaderLogoLink = styled(VersionedRouterLink)`
  display: inline-flex;
  margin-right: 24px;
`;

export const PageHeaderCartDropdownButtonHolder = styled.div`
  display: flex;
  align-items: center;
  margin-left: 6px;
  z-index: 100;
`;

export const PageHeaderNotificationsDropdownHolder = styled.div`
  @media (min-width: ${cssBreakpoints.mdUp}) {
    margin-left: 6px;
  }
`;

export const PageHeaderAuthLink = styled.a`
  padding: 0 4px;
  color: ${colors.mediumContentGrey};
  font-size: inherit;
  transition: color ${transition};

  &,
  &:hover,
  &:focus {
    text-decoration: none;
  }

  &:hover,
  &:focus {
    color: ${colors.brandBlue};
  }
`;

const pageHeaderAuthHolderDesktopGap = 16;
export const PageHeaderAuthHolder = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: ${pageHeaderAuthHolderDesktopGap}px;

  @media (max-width: ${cssBreakpoints.mdUp}) {
    margin-right: ${pageHeaderAuthHolderDesktopGap / 2}px;
  }

  a {
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 50%;
      right: -${pageHeaderAuthHolderDesktopGap / 2}px;
      transform: translateY(-50%);
      width: 1px;
      height: 20px;
      background: ${colors.elementGrey};
      opacity: 0.5;
    }

    @media (min-width: ${cssBreakpoints.mdUp}) {
      &:last-child {
        &::before {
          display: none;
        }
      }
    }
  }
`;

export const PageHeaderSignupLinkHolder = styled.div`
  @media all and (max-width: 320px) {
    display: none;
  }
`;

export const PageHeaderMobileSearchHolder = styled.div`
  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    display: none;
  }
`;

export const PageHeaderMyAccountHolder = styled.div`
  &::before {
    display: none;
  }
`;

export const PageHeaderDesktopSearchHolder = styled.div`
  padding: 16px ${gutterWidth / 2}px;
  background-color: ${colors.white};
  border-top: 1px solid ${colors.selectionGrey};

  @media all and (max-width: ${cssBreakpoints.mdDown}) {
    display: none;
  }
`;
